import { FlexBox, Icon, Text } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { ContextControl, DashboardContext } from 'lib-analytics-types';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { getFilterAssets } from '../../utils/filters/filterAssets';
import { AnalyticsFilter, AnalyticsFilterChangeCallback } from '../filter';

import { ContextMultiToggle } from './components/multiToggle/contextMultiToggle';

type AnalyticsContextProps = {
  /** The context filter definition. */
  context: DashboardContext;
  /** The currently selected context. */
  control?: ContextControl;
  /** Callback for when the context value is changed. */
  onChange: (control: ContextControl) => void;
};

export const AnalyticsContext = memo(function AnalyticsContext({
  context: { field, values, options },
  control,
  onChange,
}: AnalyticsContextProps): JSX.Element | null {
  const assets = useMemo(() => getFilterAssets(field), [field]);
  const { visualization } = options ?? {};

  const [draftValues, setDraftValues] = useState(control?.values ?? []);

  // Some context filter might support selecting multiple values, but we don't enable it by default.
  // Instead we use the `isMultiple` flag to determine whether the user has enabled multi-selection.
  const supportsMultiple = visualization === 'multi-select';

  // This is true by default, when there are more than one selected values in the store.
  const [isMultiple, setIsMultiple] = useState(false);

  /** Whether the draft values are different than the values in the store. */
  const isDirty = useMemo(
    () => !isEqual(control?.values, draftValues),
    [control?.values, draftValues],
  );

  // Updates the `isMultiple` state depending on whether the context supports multiple selection.
  useEffect(() => {
    setIsMultiple(supportsMultiple && (control?.values?.length ?? 0) > 1);
  }, [control?.values?.length, supportsMultiple]);

  // Keeps the draft values in sync with the control values in the store.
  useUpdateEffect(() => {
    if (isDirty && control) {
      setDraftValues(control?.values);
    }
  }, [control?.values]);

  /**
   * Handles toggling the isMultiple state.
   *
   * When toggling it off, we reset the selected values to the first if either
   * the selection has changed or includes more than one value.
   */
  const handleChangeIsMultiple = useCallback(
    (newIsMultiple: boolean) => {
      setIsMultiple(newIsMultiple);

      if (control && !newIsMultiple && (draftValues.length > 1 || isDirty)) {
        onChange({ ...control, values: draftValues.slice(0, 1) });
      }
    },
    [control, draftValues, isDirty, onChange],
  );

  /**
   * Handles updating the selected values.
   *
   * When selecting multiple values, we only update the internal `draftValues`
   * and then apply the change to the store when the dropdown is closed.
   *
   * When selecting a single value, the change is immediately applied and
   * reflected in the store.
   */
  const handleChange = useCallback<
    AnalyticsFilterChangeCallback<ContextControl>
  >(
    ({ filter }) => {
      if (isMultiple) {
        setDraftValues(filter.values);
        return;
      }

      if (!isEqual(filter, control)) {
        onChange(filter);
      }
    },
    [control, isMultiple, onChange],
  );

  /**
   * When the dropdown is closed, we check whether we are in multi-selection
   * mode and if any changes were made to the draft values, we update the
   * store.
   */
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (control && !isOpen && isMultiple && isDirty) {
        onChange({ ...control, values: draftValues });
      }
    },
    [control, draftValues, isDirty, isMultiple, onChange],
  );

  // Don't render anything if there are no items to select, or if the context is configured to be
  // hidden, but with preselected items.
  if (!values.length || visualization === 'hidden') {
    return null;
  }

  // Render as plain text when only a single item is available.
  if (values.length === 1) {
    return (
      <FlexBox alignItems="center" gap="250">
        <Icon name={assets.icon} color={typography.color['secondary']} />

        <Text.Paragraph variant="1" subdued>
          {values[0].label}
        </Text.Paragraph>
      </FlexBox>
    );
  }

  /**
   * The currently selected value is disabled, when the context filter is a
   * multi-select and at least one value is required to be selected.
   */
  const disabledValues =
    isMultiple && draftValues.length === 1 ? draftValues : undefined;

  const label = draftValues.length
    ? `${values.find(({ value }) => value === draftValues[0].value)?.label}${
        draftValues.length > 1 ? ` + ${draftValues.length - 1} more` : ''
      }`
    : `Select ${assets.label.toLowerCase()}`;

  return (
    <AnalyticsFilter<ContextControl>
      field={field}
      availableValues={values}
      selectedValues={draftValues}
      pinnedValues={control?.values}
      disabledValues={disabledValues}
      isMultiple={isMultiple}
      hasDeterminateSelection={false}
      onChange={handleChange}
      onToggle={handleToggle}
      buttonProps={{
        variant: 'secondary',
        icon: assets.icon,
      }}
      {...(supportsMultiple && {
        customHeaderElement: (
          <ContextMultiToggle
            value={isMultiple}
            onChange={handleChangeIsMultiple}
          />
        ),
      })}
    >
      {label}
    </AnalyticsFilter>
  );
});
